import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Package, Clock, RefreshCw, Calendar, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { isRestricted, MASK } from "../../utils/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const fmtFor = (user) => (n) => (isRestricted(user) ? MASK : _fmt(n));

const PeriodCard = ({ title, stats, color, testId, highlight, user }) => {
  const fmt = fmtFor(user);
  return (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    data-testid={testId}
    className={`rounded-2xl p-5 ${highlight ? "bg-gradient-to-br from-[#2D4A3E] to-[#1f3329] text-white" : "bg-white border border-[#E4EBE6]"}`}
  >
    <div className="flex items-center justify-between mb-3">
      <h3 className={`font-bold text-lg ${highlight ? "text-white" : "text-[#111814]"}`}>{title}</h3>
      <TrendingUp className={`w-5 h-5 ${highlight ? "text-white opacity-80" : ""}`} style={{ color: highlight ? undefined : color }} />
    </div>
    <div className="space-y-3">
      <div>
        <p className={`text-xs ${highlight ? "opacity-80" : "text-[#5C6E64]"}`}>إجمالي المبيعات</p>
        <p className={`font-bold text-2xl ${highlight ? "" : "text-[#111814]"}`}>{fmt(stats.revenue)}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className={`text-xs ${highlight ? "opacity-80" : "text-[#5C6E64]"}`}>التكلفة</p>
          <p className={`font-bold ${highlight ? "" : "text-[#4A90E2]"}`}>{fmt(stats.cost)}</p>
        </div>
        <div>
          <p className={`text-xs ${highlight ? "opacity-80" : "text-[#5C6E64]"}`}>صافي الربح</p>
          <p className={`font-bold text-xl ${highlight ? "text-[#7ED321]" : "text-[#7ED321]"}`}>{fmt(stats.profit)}</p>
        </div>
      </div>
      <div className={`flex justify-between text-xs ${highlight ? "opacity-90 border-t border-white/20" : "text-[#5C6E64] border-t border-[#E4EBE6]"} pt-3`}>
        <span>📦 {stats.orders_count} طلب</span>
        <span>🧮 {stats.total_quantity} قطعة</span>
      </div>
    </div>
  </motion.div>
  );
};

const ProfitsTab = () => {
  const { user } = useAuth();
  const fmt = fmtFor(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Date-search state for "اليوم" card. When `selectedDate` is set, the
  // card switches to historical mode and shows that specific day's numbers.
  const [selectedDate, setSelectedDate] = useState("");
  const [dateData, setDateData] = useState(null);
  const [dateLoading, setDateLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/profits`);
      setData(data);
    } catch {
      toast.error("فشل تحميل تقرير الأرباح");
    } finally {
      setLoading(false);
    }
  };

  const fetchForDate = async (iraqDate) => {
    if (!iraqDate) { setDateData(null); return; }
    setDateLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/stats-range`, { params: { iraq_date: iraqDate } });
      // Map the stats-range response shape into the PeriodCard shape.
      setDateData({
        revenue: res.sold,
        cost: 0, // Cost is not returned by stats-range; show only revenue/profit
        profit: res.profit,
        orders_count: res.orders_count,
        total_quantity: res.cartons,
        _label: res.label,
      });
    } catch {
      toast.error("فشل تحميل أرباح هذا التاريخ");
      setDateData(null);
    } finally {
      setDateLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>;
  }
  if (!data) return null;

  return (
    <div data-testid="profits-tab" className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="font-heading font-bold text-xl text-[#111814]">تقارير الأرباح والمبيعات</h2>
        <button
          onClick={fetchData}
          data-testid="profits-refresh"
          className="flex items-center gap-2 px-3 py-2 bg-[#F3F6F4] hover:bg-[#E4EBE6] rounded-full text-sm text-[#2D4A3E] transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          تحديث
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Today card with built-in date search. When admin picks a date,
            the card shows that day's numbers; "X" reverts back to today. */}
        <div className="space-y-2">
          <PeriodCard
            title={selectedDate ? `يوم ${selectedDate}` : "اليوم"}
            stats={dateData || data.today}
            color="#E85D45"
            testId="profit-today"
            highlight
            user={user}
          />
          <div className="bg-white border border-[#E4EBE6] rounded-2xl p-2 flex items-center gap-2" data-testid="profit-today-date-search">
            <Calendar className="w-4 h-4 text-[#5C6E64] shrink-0" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                fetchForDate(e.target.value);
              }}
              data-testid="profit-today-date-input"
              className="flex-1 px-2 py-1 rounded-lg border border-[#E4EBE6] text-xs focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
              placeholder="ابحث بتاريخ آخر"
            />
            {selectedDate && (
              <button
                type="button"
                onClick={() => { setSelectedDate(""); setDateData(null); }}
                data-testid="profit-today-date-clear"
                title="رجّع لليوم الحالي"
                className="w-7 h-7 rounded-full bg-[#F3F6F4] hover:bg-[#E4EBE6] flex items-center justify-center text-[#5C6E64] shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {dateLoading && <span className="text-[10px] text-[#5C6E64]">جاري...</span>}
          </div>
        </div>
        <PeriodCard title="هذا الشهر" stats={data.month} color="#4A90E2" testId="profit-month" user={user} />
        <PeriodCard title="إجمالي" stats={data.total} color="#7ED321" testId="profit-total" user={user} />
      </div>

      {/* Pending highlight */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="profit-pending"
        className="bg-gradient-to-r from-[#F5A623]/15 to-[#F5A623]/5 border border-[#F5A623]/30 rounded-2xl p-5 flex justify-between items-center"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
            <Clock className="w-6 h-6 text-[#F5A623]" />
          </div>
          <div>
            <p className="text-xs text-[#5C6E64]">طلبات قيد المعالجة (لم تُحسب في الأرباح بعد)</p>
            <p className="font-bold text-lg text-[#111814]">
              {data.pending.count} طلب — قيمتها {fmt(data.pending.revenue)} د.ع
            </p>
          </div>
        </div>
      </motion.div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E4EBE6] rounded-2xl p-4">
          <DollarSign className="w-5 h-5 text-[#7ED321] mb-2" />
          <p className="text-xs text-[#5C6E64]">مبيعات اليوم</p>
          <p className="font-bold text-lg text-[#111814]">{fmt(data.today.revenue)}</p>
        </div>
        <div className="bg-white border border-[#E4EBE6] rounded-2xl p-4">
          <Package className="w-5 h-5 text-[#4A90E2] mb-2" />
          <p className="text-xs text-[#5C6E64]">قطع بِيعت اليوم</p>
          <p className="font-bold text-lg text-[#111814]">{data.today.total_quantity}</p>
        </div>
        <div className="bg-white border border-[#E4EBE6] rounded-2xl p-4">
          <TrendingUp className="w-5 h-5 text-[#E85D45] mb-2" />
          <p className="text-xs text-[#5C6E64]">ربح اليوم</p>
          <p className="font-bold text-lg text-[#7ED321]">{fmt(data.today.profit)}</p>
        </div>
        <div className="bg-white border border-[#E4EBE6] rounded-2xl p-4">
          <TrendingUp className="w-5 h-5 text-[#2D4A3E] mb-2" />
          <p className="text-xs text-[#5C6E64]">ربح الشهر</p>
          <p className="font-bold text-lg text-[#7ED321]">{fmt(data.month.profit)}</p>
        </div>
      </div>
    </div>
  );
};

export default ProfitsTab;
